import { useCallback, useRef, useState } from 'react';
import { useSnackbar } from 'notistack';
import ConfirmarEnvioFacturaEmailDialog from '../components/ventas/ConfirmarEnvioFacturaEmailDialog';
import EnviarFacturaEmailDialog from '../components/ventas/EnviarFacturaEmailDialog';
import facturaVentaService, {
  type FacturaVentaEnvioEmailPreview,
  type FacturaVentaEnvioEmailRequest,
  type FacturaVentaPostEmisionEmailOffer,
} from '../services/facturaVentaService';
import { getApiErrorMessage } from '../utils/apiError';
import {
  debePreguntarEnvioPostEmision,
  normalizePostEmisionOffer,
  offerFromEnvioPreview,
  puedeEnviarDirectoAlCliente,
} from '../utils/postEmisionEnvioFacturaEmail';

/**
 * Tras emitir una factura: si el tenant tiene SMTP, pregunta si enviar mail.
 * Con email de cliente → envío directo. Sin email → diálogo de envío existente.
 */
export function usePostEmisionEnvioFacturaEmail() {
  const { enqueueSnackbar } = useSnackbar();
  const offeredRef = useRef<number | null>(null);
  const inFlightRef = useRef(false);

  const [confirmOpen, setConfirmOpen] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [nroFormulario, setNroFormulario] = useState<number | null>(null);
  const [offer, setOffer] = useState<FacturaVentaPostEmisionEmailOffer | null>(null);
  const [preview, setPreview] = useState<FacturaVentaEnvioEmailPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [sending, setSending] = useState(false);

  const reset = useCallback(() => {
    setConfirmOpen(false);
    setDialogOpen(false);
    setNroFormulario(null);
    setOffer(null);
    setPreview(null);
    setPreviewLoading(false);
    setSending(false);
  }, []);

  const resetOfferedTracking = useCallback(() => {
    offeredRef.current = null;
    inFlightRef.current = false;
    reset();
  }, [reset]);

  /** Libera el lock para poder re-ofrecer (p. ej. tras cobro pendiente en POS). */
  const allowOfferAgain = useCallback(() => {
    offeredRef.current = null;
    inFlightRef.current = false;
    setConfirmOpen(false);
    setOffer(null);
  }, []);

  const ofrecerTrasEmision = useCallback(async (nro: number) => {
    const id = Number(nro) || 0;
    if (id <= 0) return;
    if (offeredRef.current === id || inFlightRef.current) return;

    inFlightRef.current = true;
    try {
      let oferta: FacturaVentaPostEmisionEmailOffer | null = null;

      try {
        const raw = await facturaVentaService.getOfertaEnvioEmailPostEmision(id);
        oferta = normalizePostEmisionOffer(raw as unknown as Record<string, unknown>);
      } catch (error: unknown) {
        console.warn(
          'Oferta post-emisión no disponible; se usa preview de envío.',
          error,
        );
        const p = await facturaVentaService.getEnvioEmailPreview(id);
        oferta = offerFromEnvioPreview(p);
      }

      if (!debePreguntarEnvioPostEmision(oferta)) {
        offeredRef.current = id;
        return;
      }

      offeredRef.current = id;
      setNroFormulario(id);
      setOffer(oferta);
      setConfirmOpen(true);
    } catch (error: unknown) {
      console.error('No se pudo evaluar oferta de envío post-emisión:', error);
    } finally {
      inFlightRef.current = false;
    }
  }, []);

  const handleNoEnviar = useCallback(() => {
    setConfirmOpen(false);
    setOffer(null);
  }, []);

  const abrirDialogoEnvio = useCallback(async (id: number) => {
    setDialogOpen(true);
    setPreview(null);
    setPreviewLoading(true);
    try {
      const p = await facturaVentaService.getEnvioEmailPreview(id);
      setPreview(p);
    } catch (error: unknown) {
      console.error('Error al cargar datos de envío por mail:', error);
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudieron cargar los datos para el envío por mail'), {
        variant: 'error',
      });
      setDialogOpen(false);
      setNroFormulario(null);
    } finally {
      setPreviewLoading(false);
    }
  }, [enqueueSnackbar]);

  const handleConfirmarEnviar = useCallback(async () => {
    if (!nroFormulario || !offer) return;

    if (!puedeEnviarDirectoAlCliente(offer)) {
      setConfirmOpen(false);
      await abrirDialogoEnvio(nroFormulario);
      return;
    }

    try {
      setSending(true);
      try {
        const result = await facturaVentaService.enviarFacturaPorEmailAlCliente(nroFormulario);
        enqueueSnackbar(result.mensaje ?? 'Factura enviada por correo correctamente', {
          variant: 'success',
        });
      } catch (directError: unknown) {
        const status = (directError as { response?: { status?: number } })?.response?.status;
        if (status !== 404) throw directError;
        const result = await facturaVentaService.enviarFacturaPorEmail(nroFormulario, {
          destinatarios: offer.emailsSugeridos,
        });
        enqueueSnackbar(result.mensaje ?? 'Factura enviada por correo correctamente', {
          variant: 'success',
        });
      }
      setConfirmOpen(false);
      setOffer(null);
    } catch (error: unknown) {
      console.error('Error al enviar factura por mail (post-emisión):', error);
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo enviar la factura por correo'), {
        variant: 'error',
      });
    } finally {
      setSending(false);
    }
  }, [abrirDialogoEnvio, enqueueSnackbar, nroFormulario, offer]);

  const handleCloseEmailDialog = useCallback(() => {
    setDialogOpen(false);
    setPreview(null);
    setNroFormulario(null);
  }, []);

  const handleEnviarDesdeDialogo = useCallback(async (payload: FacturaVentaEnvioEmailRequest) => {
    if (!nroFormulario) return;
    try {
      setSending(true);
      const result = await facturaVentaService.enviarFacturaPorEmail(nroFormulario, payload);
      enqueueSnackbar(result.mensaje ?? 'Factura enviada por correo correctamente', {
        variant: 'success',
      });
      handleCloseEmailDialog();
    } catch (error: unknown) {
      console.error('Error al enviar factura por mail:', error);
      enqueueSnackbar(getApiErrorMessage(error, 'No se pudo enviar la factura por correo'), {
        variant: 'error',
      });
    } finally {
      setSending(false);
    }
  }, [enqueueSnackbar, handleCloseEmailDialog, nroFormulario]);

  const emailUi = (
    <>
      <ConfirmarEnvioFacturaEmailDialog
        open={confirmOpen}
        sending={sending}
        clienteTieneEmail={Boolean(offer?.clienteTieneEmail)}
        emailsSugeridos={offer?.emailsSugeridos ?? []}
        nombreCliente={offer?.nombreCliente}
        comprobanteLabel={offer?.comprobanteLabel}
        onNoEnviar={handleNoEnviar}
        onEnviar={() => { void handleConfirmarEnviar(); }}
      />
      <EnviarFacturaEmailDialog
        open={dialogOpen}
        facturaNumero={nroFormulario}
        preview={preview}
        loading={previewLoading}
        sending={sending}
        onClose={handleCloseEmailDialog}
        onSend={(payload) => { void handleEnviarDesdeDialogo(payload); }}
        onValidationError={(msg) => enqueueSnackbar(msg, { variant: 'warning' })}
      />
    </>
  );

  return {
    ofrecerTrasEmision,
    resetOfferedTracking,
    allowOfferAgain,
    emailUi,
  };
}
